import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, CreditCard, Star } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export const SubscriptionManager = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managingPlan, setManagingPlan] = useState(false);
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeStep, setDowngradeStep] = useState("offer");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get("/subscription/status");
      setSub(res.data);
    } catch (err) {
      // silently ignore; card will show nothing actionable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleManage = async () => {
    try {
      const res = await api.post("/subscription/portal", { origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal.");
    }
  };

  const handleCancel = async () => {
    setManagingPlan(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You'll move to Pocket BA Lite when your access period ends.");
      setDowngradeOpen(false);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel subscription.");
    } finally {
      setManagingPlan(false);
    }
  };

  const handleResume = async () => {
    setManagingPlan(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — you'll keep full access.");
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resume subscription.");
    } finally {
      setManagingPlan(false);
    }
  };

  const handleWinBack = async () => {
    setManagingPlan(true);
    try {
      await api.post("/subscription/win-back");
      toast.success("Offer applied — 50% off your next 3 months. Thanks for staying!");
      setDowngradeOpen(false);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply the offer.");
    } finally {
      setManagingPlan(false);
    }
  };

  const openDowngrade = () => {
    setDowngradeStep("offer");
    setDowngradeOpen(true);
  };

  const reason = sub?.reason;
  const isTrialing = sub?.is_trialing;
  const trialEnd = sub?.trial_end;
  const currentInterval = sub?.current_interval;
  const cancelAtPeriodEnd = sub?.cancel_at_period_end;
  const periodEnd = sub?.current_period_end;

  return (
    <Card data-testid="subscription-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Subscription
        </CardTitle>
        <CardDescription>Manage your AI Buyers Agent plan and billing</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your subscription…
          </div>
        ) : reason === "subscription" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {isTrialing ? (
                <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Free trial</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Subscribed</Badge>
              )}
              <Badge variant="secondary" data-testid="settings-plan-interval-badge">
                {currentInterval === "year" ? "Annual" : "Monthly"}
              </Badge>
            </div>

            {isTrialing && trialEnd && !cancelAtPeriodEnd && (
              <p className="text-sm text-slate-500" data-testid="settings-trial-line">
                Free trial ends {formatDate(trialEnd)}.
              </p>
            )}
            {!isTrialing && periodEnd && !cancelAtPeriodEnd && (
              <p className="text-sm text-slate-500" data-testid="settings-renew-line">
                Renews {currentInterval === "year" ? "annually" : "monthly"} on {formatDate(periodEnd)}.
              </p>
            )}
            {cancelAtPeriodEnd && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="settings-cancel-notice">
                Your subscription is set to cancel. You'll keep access until{" "}
                {isTrialing && trialEnd ? formatDate(trialEnd) : formatDate(periodEnd)}, then move to Pocket BA Lite.
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleManage} data-testid="settings-manage-sub-btn">
                Manage subscription
              </Button>
              {cancelAtPeriodEnd ? (
                <Button variant="outline" size="sm" onClick={handleResume} disabled={managingPlan} data-testid="settings-resume-sub-btn">
                  {managingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resume subscription"}
                </Button>
              ) : (
                <AlertDialog open={downgradeOpen} onOpenChange={setDowngradeOpen}>
                  <Button variant="ghost" size="sm" className="text-slate-500" onClick={openDowngrade} data-testid="settings-downgrade-btn">
                    Downgrade to Lite
                  </Button>
                  <AlertDialogContent data-testid="settings-downgrade-dialog">
                    {downgradeStep === "offer" ? (
                      <>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-emerald-600" /> Wait — here's 50% off
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Before you go, stay subscribed and get{" "}
                            <span className="font-semibold text-slate-900">50% off your next 3 months</span> of the AI
                            Buyers Agent. Keep unlimited reports, price &amp; rental estimates and AI chat — for half the price.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <Button variant="ghost" onClick={() => setDowngradeStep("confirm")} disabled={managingPlan} data-testid="settings-decline-winback-btn">
                            No thanks, downgrade
                          </Button>
                          <Button onClick={handleWinBack} disabled={managingPlan} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-claim-winback-btn">
                            {managingPlan ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</> : "Claim 50% off"}
                          </Button>
                        </AlertDialogFooter>
                      </>
                    ) : (
                      <>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Downgrade to Pocket BA Lite?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll keep full AI Buyers Agent access until{" "}
                            {isTrialing && trialEnd
                              ? formatDate(trialEnd)
                              : periodEnd
                              ? formatDate(periodEnd)
                              : "the end of your current period"}
                            , then your account moves to <span className="font-medium">Pocket BA Lite</span> — free access to
                            everything except the AI Buyers Agent. You can resubscribe anytime.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="settings-downgrade-cancel-btn">Keep my subscription</AlertDialogCancel>
                          <AlertDialogAction onClick={handleCancel} data-testid="settings-downgrade-confirm-btn">
                            Downgrade to Lite
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </>
                    )}
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ) : reason === "admin" ? (
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Admin access</Badge>
            <p className="text-sm text-slate-500">You have full access to every AI Buyers Agent feature.</p>
          </div>
        ) : reason === "referral_free" ? (
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Included via partner</Badge>
            <p className="text-sm text-slate-500">Your access is included through your partner — no billing required.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Pocket BA Lite</Badge>
              <p className="text-sm text-slate-500">
                You have free access to everything except the AI Buyers Agent.
              </p>
            </div>
            <Button
              className="bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={() => navigate("/property-researcher")}
              data-testid="settings-upgrade-btn"
            >
              <Star className="w-4 h-4 mr-2" /> Upgrade to AI Buyers Agent
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
